/**
 * Mobile Navigation Drawer
 * 
 * Provides navigation for mobile users via a slide-out drawer.
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Home, List, Settings, DollarSign,
  Trello, Target, Database, Lightbulb, AlertCircle,
  RefreshCw, Bot, ClipboardList, Car
} from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { cn } from '../../lib/utils';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: Home },
  { name: 'Acquisition Pipeline', href: '/pipeline', icon: Trello },
  { name: 'Programs', href: '/programs', icon: Car },
  { name: 'Parts', href: '/opportunities', icon: List },
  { name: 'S&OP Planning', href: '/planning', icon: Lightbulb },
  { name: 'Claims / Recovery', href: '/claims', icon: AlertCircle },
  { name: 'Approvals / Tasks', href: '/my-tasks', icon: ClipboardList },
  { name: 'Market Forecasts', href: '/volume-management', icon: RefreshCw },
  { name: 'Import / Data Hub', href: '/import', icon: Database },
  { name: 'Goals', href: '/goals', icon: Target },
  { name: 'Tariff Rates', href: '/tariff-rates', icon: DollarSign },
  { name: 'Flo AI', href: '/flo', icon: Bot },
  { name: 'Settings', href: '/settings', icon: Settings },
];

const MobileNav = ({ isOpen, onClose }) => {
  const location = useLocation();

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="left" className="w-72 p-0">
        <SheetHeader className="p-4 border-b dark:border-gray-800">
          <SheetTitle className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-700 text-white font-bold text-lg">
              AF
            </div>
            <div>
              <span className="text-lg font-semibold">AutoFlo</span>
            </div>
          </SheetTitle>
        </SheetHeader>
        
        <nav className="flex-1 space-y-1 p-4 overflow-y-auto max-h-[calc(100vh-80px)]">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            const isFloAI = item.href === '/flo';
            
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={onClose}
                className={cn(
                  'group flex items-center rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200',
                  isActive
                    ? 'bg-gray-900 dark:bg-blue-600 text-white shadow-sm'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                )}
              >
                <item.icon
                  className={cn(
                    'h-5 w-5 flex-shrink-0 mr-3 transition-colors',
                    isActive 
                      ? 'text-white' 
                      : isFloAI 
                        ? 'text-[#07689f]' 
                        : 'text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200'
                  )}
                />
                <span>{item.name}</span>
              </Link>
            );
          })}
        </nav>
      </SheetContent>
    </Sheet>
  );
};

export default MobileNav;
